import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

const normalizeOrder = (value) => {
  const order = Number(value);
  return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
};

const compareLabels = (left, right) =>
  String(left || "").localeCompare(String(right || ""), "fr", {
    sensitivity: "base",
  });

const getProcessusLabel = (processus) => {
  const instance = String(processus.instance || "Référence non renseignée").trim();
  const referentiel = String(
    processus.referentiel_nom || "Référentiel non renseigné"
  ).trim();
  const direction = String(processus.direction_code || "—").trim();
  return `${instance} — ${referentiel} (${direction})`;
};

const groupProcessus = (processus) => {
  const groupsByCategory = new Map();

  processus.forEach((item) => {
    const categoryId = item.categorie_id ?? "unknown";
    const key = String(categoryId);

    if (!groupsByCategory.has(key)) {
      groupsByCategory.set(key, {
        id: categoryId,
        name: item.categorie_name || "Autres processus",
        order: normalizeOrder(item.categorie_ordre),
        processus: [],
      });
    }

    groupsByCategory.get(key).processus.push(item);
  });

  return [...groupsByCategory.values()]
    .sort(
      (left, right) =>
        left.order - right.order || compareLabels(left.name, right.name)
    )
    .map((group) => ({
      ...group,
      processus: [...group.processus].sort(
        (left, right) =>
          compareLabels(left.referentiel_nom, right.referentiel_nom) ||
          compareLabels(left.direction_code, right.direction_code)
      ),
    }));
};

const navigationButtonSx = {
  borderColor: "#c5d3e5",
  borderRadius: "10px",
  color: "#586d93",
  fontSize: 14,
  fontWeight: 700,
  minHeight: 46,
  px: 2.5,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f7faff",
    borderColor: "#8fa7c5",
  },
};

const ProcessusSelection = ({
  error,
  loading,
  onRetry,
  onSelect,
  processus,
  selectedProcessusId,
}) => {
  const navigate = useNavigate();
  const groups = useMemo(() => groupProcessus(processus), [processus]);

  const handleChange = (event) => {
    const selected = processus.find(
      (item) => String(item.processus_id) === String(event.target.value)
    );
    onSelect(selected || null);
  };

  const empty = !loading && !error && processus.length === 0;

  return (
    <Box
      aria-label="Sélection du processus concerné"
      role="region"
      sx={{
        alignItems: "center",
        backgroundColor: "#f1f5fb",
        color: "#111d33",
        display: "flex",
        justifyContent: "center",
        minHeight: "calc(100vh - 160px)",
      }}
    >
      <Box sx={{ maxWidth: 780, textAlign: "center", width: "100%" }}>
        <Box aria-hidden="true" sx={{ fontSize: { xs: 56, sm: 68 }, lineHeight: 1, color: "#273c62" }}>
          <TrackChangesIcon sx={{ fontSize: { xs: 56, sm: 68 } }} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 25, sm: 31 },
            fontWeight: 800,
            letterSpacing: "-0.02em",
            mt: 3.5,
          }}
        >
          Choisir le processus concerné
        </Typography>

        <FormControl
          disabled={loading || Boolean(error) || empty}
          fullWidth
          size="small"
          sx={{ maxWidth: 425, mt: 4.2 }}
        >
          <Select
            displayEmpty
            inputProps={{ "aria-label": "Choisir le processus concerné" }}
            onChange={handleChange}
            renderValue={(value) => {
              if (!value) {
                if (loading) return "Chargement des processus…";
                if (empty) return "Aucun processus disponible";
                if (error) return "Processus indisponibles";
                return "— Choisir un processus —";
              }

              const selected = processus.find(
                (item) => String(item.processus_id) === String(value)
              );
              return selected ? getProcessusLabel(selected) : "— Choisir un processus —";
            }}
            startAdornment={
              loading ? <CircularProgress size={17} sx={{ mr: 1 }} /> : null
            }
            sx={{
              backgroundColor: "#fff",
              borderRadius: "14px",
              color: "#16243a",
              fontSize: 15,
              height: 50,
              textAlign: "left",
              "& .MuiSelect-select": {
                alignItems: "center",
                display: "flex",
                py: 1.4,
              },
            }}
            value={selectedProcessusId}
          >
            <MenuItem disabled value="">
              — Choisir un processus —
            </MenuItem>
            {groups.flatMap((group) => [
                <ListSubheader
                  key={`category-${group.id}`}
                  sx={{
                    backgroundColor: "#edf3f8",
                    color: "#173d61",
                    fontSize: 11,
                    fontWeight: 800,
                    lineHeight: "34px",
                    textTransform: "uppercase",
                  }}
                >
                  {group.name}
                </ListSubheader>,
                ...group.processus.map((item) => (
                  <MenuItem
                    key={item.processus_id}
                    sx={{ fontSize: 13, whiteSpace: "normal" }}
                    value={item.processus_id}
                  >
                    {getProcessusLabel(item)}
                  </MenuItem>
                )),
              ])}
          </Select>
        </FormControl>

        {error && (
          <Alert
            action={
              <Button color="inherit" onClick={onRetry} size="small">
                Réessayer
              </Button>
            }
            severity="error"
            sx={{ mx: "auto", mt: 2, textAlign: "left", maxWidth: 550 }}
          >
            {error}
          </Alert>
        )}

    
        <Typography
          color="#5c7198"
          sx={{ fontSize: { xs: 14, sm: 16 }, lineHeight: 1.7, mt: 1.2 }}
        >
          Pour créer une nouvelle NC, sélectionnez d'abord le processus concerné.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Button
            onClick={() => navigate("/smq_nc/dashboard")}
            sx={navigationButtonSx}
            variant="outlined"
          >
            ← Retour au tableau de bord
          </Button>
          <Button
            onClick={() => navigate("/smq_nc/registre")}
            startIcon={<span aria-hidden="true">📋</span>}
            sx={navigationButtonSx}
            variant="outlined"
          >
            Registre des NC
          </Button>
          <Button
            onClick={() => navigate("/smq_nc/analyser")}
            startIcon={<span aria-hidden="true">🔬</span>}
            sx={navigationButtonSx}
            variant="outlined"
          >
            Analyser NC
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

ProcessusSelection.defaultProps = {
  error: null,
  selectedProcessusId: "",
};

ProcessusSelection.propTypes = {
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  processus: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedProcessusId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ProcessusSelection;
